'use client';

import type { ActionKitImpAction } from '../action-kit-imp';
import type {
  ActionKitErrorBase,
  InferActionKitErrorCodes,
  InferActionKitErrorTypeForCode,
} from '../action-kit/types';
import { createFetchContext } from '../fetch-context/createFetchContext';
import type { LoaderOptions } from '../loader/types';
import React, { ReactElement, ReactNode } from 'react';
import { DisplayInfo, Loading } from '@octano/global-ui';

export function createLoader<
  Result,
  Args extends [] | [any] | [any, any] = [],
  TError extends ActionKitErrorBase = ActionKitErrorBase,
  TActionError extends ActionKitErrorBase | undefined = undefined
>({ action, options }: LoaderOptions<Result, Args, TError, TActionError>) {
  const { FetchProvider, FetchConsumer, useFetch } = createFetchContext(
    action,
    options,
  );

  const useLoader = () => {
    return useFetch();
  };

  const useLoaderData = () => {
    const data = useFetch();
    if (data.loading || !data.result) {
      throw new Error('Aún está cargando o no hay datos');
    }
    if (!data.result.success) {
      throw new Error('Error al cargar los datos: ' + data.result.data.code);
    }
    return data.result.data;
  };

  const LoaderConsumerSimple = (
    props: {
      slotNoResult?: ReactElement;
      children?: ReactNode;
    } & React.JSX.IntrinsicAttributes,
  ): ReactElement => {
    return (
      <FetchConsumer>
        {({ loading, result }) => {
          if (loading) {
            return <Loading textBody="" />;
          }
          if (!result) {
            if (props.slotNoResult !== undefined) {
              return props.slotNoResult;
            }
            return (
              <DisplayInfo
                title="No hay resultado"
                textBody="No hay un resultado para mostrar."
              />
            );
          }
          if (!result.success) {
            const errorCode = result.data.code as InferActionKitErrorCodes<
              ReturnType<
                ActionKitImpAction<(...args: any) => Promise<any>, TError>
              > extends Promise<infer R>
                ? R extends { success: false; data: infer E }
                  ? E
                  : never
                : never
            >;

            const errorHandler = options.onError[errorCode];
            if (!errorHandler) {
              throw new Error(
                `No error handler definido para el código de error: ${errorCode}.`,
              );
            }

            return errorHandler(
              result.data as InferActionKitErrorTypeForCode<
                Exclude<TError, undefined>,
                typeof errorCode
              >,
            );
          }

          return props.children;
        }}
      </FetchConsumer>
    );
  };

  return {
    useLoader,
    useLoaderData,
    LoaderConsumer: FetchConsumer,
    LoaderProvider: FetchProvider,
    LoaderConsumerSimple,
  };
}
